import { AfterViewInit, Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';

import { combineLatest, filter, merge, Observable, Subscription, tap, take, skip } from 'rxjs';

import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '@Common/Charts/components/CardChart/card-chart.component';
import { ChartModel } from '@Common/Charts/models/chart.model';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { MADSentimentSeriesTypesEnum } from '@Monitor/Shared/enums/mad-series-types.enum';
import { MADSeriesMap } from '@Monitor/Shared/services/mad-charts.service';
import { SocialSeriesMetricsEnum } from '@Social/Intelligence/enums/social-series-metrics.enum';
import { TopicsService } from '@Monitor/Core/services/topics.service';
import { SentimentModel } from '@Monitor/Shared/models/sentiment.model';
import { MADSentimentService } from './mad-sentiment.service';
import { ChartStylesModel } from '@Common/Charts/models/chart-styles.model';
import { MapChartColorSchemeEnum, MapChartModel } from '@Common/Charts/models/map-chart.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { AbstractChartOptionsModel } from '@Common/Charts/models/abstract-chart-options.model';
import { ChartMetadataModel } from '@Common/Charts/models/chart-metadata.model';
import { ReportBlockTypeEnum } from '@Common/Reports/enums/report-block-type.enum';
import { chartColors } from '@Common/Charts/enums/chart-colors.enum';
import { DropdownTypesEnum } from '@Common/Forms/enums/dropdown-types.enum';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { MADChartOptionsEnum } from '@Monitor/Shared/enums/mad-chart-options.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { ReportsChartDetailService } from '@Common/Reports/services/reports-chart-detail.service';
import { CategoryValueXYChart } from '@Common/Charts/models/category-value-x-y-chart.model';
import { CategoryValueXYChartOptions, StackTypesEnum } from '@Common/Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartSpecificTypesEnum } from '@Common/Reports/enums/chart-specific-types.enum';
import { ReportsChartSeriesConverterService } from '@Common/Reports/services/reports-chart-series-converter.service';
import { MediaTypeModel } from '@Monitor/Shared/models/media-type.model';
import { stackedBarColorsConfig } from '@Common/Charts/config/stacked-bar-colors.config';
import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { LabelDisplayOptionsEnum } from '@Common/Misc/enums/label-display-options.enum';
import { FolderService } from '@Monitor/Core/services/folder.service';
import { MediaSubscriptionService } from '@Monitor/Core/services/media-subscription.service';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { DateAndValueXYChartUnitEnum } from '@Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { WordCloudChartModel } from '@Common/Charts/models/WordCloudChart/word-cloud-chart.model';
import { WordCloudSeriesModel } from '@Common/Charts/models/WordCloudChart/word-cloud-series.model';
import { MonitoringKeywordGroupModel } from '@Monitor/Shared/models/monitoring-keyword-group.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { WordCloudChartOptionsModel } from '@Common/Charts/models/WordCloudChart/word-cloud-chart-options.model';
import { SocialMetricAnalysisTypesEnum } from '@Common/Charts/enums/social-analysis-types.enum';
import { TopTermEvent } from '@Common/Charts/components/TopTermsChart/top-terms-chart.component';
import { MonitoringDashboardSearchService } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { BlockModel } from '@Common/PageBuilder/models/block.model';
import { topTermsListBlockType } from '@Social/Intelligence/models/top-terms-list-block-type.model';
import { TopTermsListChartDisplayElementModel } from '@Social/Intelligence/models/top-terms-list-chart-display-element.model';
import { ChartDisplaySettingsModel } from '@Common/Charts/models/chart-display-settings.model';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { ReportSeriesFacetGeneratorEnum } from '@Common/Reports/models/report-series-facet-generator.enum';
import { TermTypesEnum } from '@Common/Reports/enums/term-types.enum';
import { StackedChartTypesEnum } from '@Common/Reports/enums/stacked-chart-types.enum';
import { ChartTimeIntervalsEnum } from '@Common/Reports/enums/chart-time-intervals.enum';

import { ChartExportEventModel } from '@Common/Charts/models/chart-export-event.model';
import * as am4core from '@amcharts/amcharts4/core';
import { SocialChartExportModel, SocialChartExportService } from '@Social/Intelligence/services/social-chart-export.service';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { reportBlockTypesMap } from '@Common/Reports/models/reports-block-types.model';
import { BlobType } from '@zumer/snapdom';
import { topMentionsMADExportStyleOverrides } from '@Monitor/Shared/components/TopMentionsMAD/top-mentions-mad-chart.component';
import { Am5ChartConfigurationService } from '@Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartTooltipService } from '@Common/Charts/services/am5-chart-tooltip.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { Am5DateValueXYChart } from '@Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { UserCurrency } from '@Core/Users/enums/user-currency';
import { ReportDateRangeModel } from '@Common/Reports/models/report-date-range.model';
import { Am5ChartSettingsService } from '@Common/Reports/services/am5-chart-settings.service';


@Component({
    selector: 'agpr-mad-sentiment',
    templateUrl: './mad-sentiment.component.html',
    styleUrls: ['./mad-sentiment.component.scss'],
    providers: [Am5ChartTooltipService],
    standalone: false
})
export class MADSentimentComponent implements OnInit, OnDestroy, AfterViewInit {

    @Output() reportSeriesReady: EventEmitter<MADSeriesMap> = new EventEmitter<MADSeriesMap>();
    @Output() reportSeriesChange: EventEmitter<{oldSeriesTypes: any[]; newSeries: ReportSeriesModel[]}> = new EventEmitter();

    seriesMap: MADSeriesMap;
    classToChartModelMap: Map<string, ChartModel | Am5Chart>;
    classToReportSeriesMap: Map<string, ReportSeriesModel[]>;
    baseMediaTypes: MediaTypeModel[] = [];
    currencyCode: UserCurrency;

    positiveCardSeries: ReportSeriesModel[] = [];
    positiveCardSeriesTypes = [MADSentimentSeriesTypesEnum.positive, MADSentimentSeriesTypesEnum.positivePreviousPeriod];
    neutralCardSeries: ReportSeriesModel[] = [];
    neutralCardSeriesTypes = [MADSentimentSeriesTypesEnum.neutral, MADSentimentSeriesTypesEnum.neutralPreviousPeriod];
    negativeCardSeries: ReportSeriesModel[] = [];
    negativeCardSeriesTypes = [MADSentimentSeriesTypesEnum.negative, MADSentimentSeriesTypesEnum.negativePreviousPeriod];

    sentimentByMediaTypeChart: CategoryValueXYChart;
    sentimentByMediaTypeChartClass: string = 'agpr-madSentiment-sentimentByMediaTypeChart';
    sentimentByMediaTypeChartNoData: boolean = false;
    sentimentByMediaTypeChartStackType: StackTypesEnum;
    selectedSentimentByMediaChartTabIndex: number = 0;
    selectedSentimentTrendChartTabIndex: number = 0;

    positiveMapChart: MapChartModel;
    positiveMapChartClass: string = 'agpr-madSentiment-positiveMapChart';
    selectedPositiveMapChartDropdownOption: DropdownOptionModel;
    positiveMapChartNoData: boolean = false;

    negativeMapChart: MapChartModel;
    negativeMapChartClass: string = 'agpr-madSentiment-negativeMapChart';
    selectedNegativeMapChartDropdownOption: DropdownOptionModel;
    negativeMapChartNoData: boolean = false;

    topMentionsSeries: ReportSeriesModel;
    selectedTopMentionsSentiment: MADChartOptionsEnum = MADChartOptionsEnum.POSITIVE;
    selectedTopMentionsSortMetric: MADChartOptionsEnum = MADChartOptionsEnum.IMPRESSIONS;

    positiveTopTermsSeries: ReportSeriesModel;
    negativeTopTermsSeries: ReportSeriesModel;
    positiveWordCloudChart: WordCloudChartModel;
    positiveWordCloudSeries: WordCloudSeriesModel;
    negativeWordCloudChart: WordCloudChartModel;
    negativeWordCloudSeries: WordCloudSeriesModel;
    positiveWordCloudChartClass: string = 'agpr-madSentiment-positiveWordCloudChart';
    negativeWordCloudChartClass: string = 'agpr-madSentiment-negativeWordCloudChart';
    currentSelectedPositiveTerm: string = '';
    currentSelectedNegativeTerm: string = '';
    topTermsChartOptions: WordCloudChartOptionsModel = {};
    // Sentiment Trend
    sentimentTrendChart: Am5DateValueXYChart;
    sentimentTrendSeries: ReportSeriesModel[];
    sentimentTrendChartNoData: boolean = false;
    sentimentTrendChartClass: string = 'agpr-madSentiment-sentimentTrendChart';

    positiveOutletsSeries: ReportSeriesModel;
    selectedPositiveOutletsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    positiveOutletsBlock: BlockModel;
    negativeOutletsSeries: ReportSeriesModel;
    selectedNegativeOutletsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    negativeOutletsBlock: BlockModel;
    positiveAuthorsSeries: ReportSeriesModel;
    selectedPositiveAuthorsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    positiveAuthorsBlock: BlockModel;
    negativeAuthorsSeries: ReportSeriesModel;
    selectedNegativeAuthorsMetric: ChartMetricsEnum = ChartMetricsEnum.VOLUME;
    negativeAuthorsBlock: BlockModel;

    negativeTopTermSubscription: Subscription;
    positiveTopTermSubscription: Subscription;
    subscriptions: Subscription[] = [];

    readonly cardChartComponentHighlightColors = CardChartComponentHighlightColorEnum;
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly cardChartLayoutTypes = CardChartComponentLayoutEnum;
    readonly dropdownTypes = DropdownTypesEnum;
    readonly agprComponentStyles = AgprComponentStyleEnum;
    readonly mapChartDropdownOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.VOLUME}`, MADChartOptionsEnum.VOLUME),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`, MADChartOptionsEnum.IMPRESSIONS),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`, MADChartOptionsEnum.AVE)
    ];
    readonly fullOptions: DropdownOptionModel[] = [
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.VOLUME}`, MADChartOptionsEnum.VOLUME),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.IMPRESSIONS}`, MADChartOptionsEnum.IMPRESSIONS),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.AVE}`, MADChartOptionsEnum.AVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.POSITIVE}`, MADChartOptionsEnum.POSITIVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.NEGATIVE}`, MADChartOptionsEnum.NEGATIVE),
        new DropdownOptionModel(`Monitor.MAD.dropdownOptions.${MADChartOptionsEnum.DOMAIN_AUTHORITY}`, MADChartOptionsEnum.DOMAIN_AUTHORITY)
    ];

    readonly messageSeverities = messageSeverityEnum;
    readonly madSentimentSeriesTypes = MADSentimentSeriesTypesEnum;
    readonly stackTypes = StackTypesEnum;
    readonly sentimentsToChartColorsMap = new Map<SearchCriteriaQuerySentimentsEnum, chartColors>([
        [SearchCriteriaQuerySentimentsEnum.positive, chartColors.agprPositive],
        [SearchCriteriaQuerySentimentsEnum.neutral, chartColors.agprNeutral],
        [SearchCriteriaQuerySentimentsEnum.negative, chartColors.agprNegative],
        [SearchCriteriaQuerySentimentsEnum.notAvailable, chartColors.agprNotAvailable]
    ]);
    readonly searchCriteriaQuerySentimentsEnum = SearchCriteriaQuerySentimentsEnum;
    readonly entityType = EntityType;
    readonly madSentimentChartTypeEnum = ReportBlockTypeEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];

    constructor(
        public topicsService: TopicsService,
        public folderService: FolderService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public madSentimentService: MADSentimentService,
        public reportsChartDetailService: ReportsChartDetailService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public userPreferencesService: UserPreferencesService,
        public localizationService: LocalizationService,
        public dashboardService: MonitoringDashboardSearchService,
        public chartExportService: SocialChartExportService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
        public currentUserStateService: CurrentUserStateService
    ) {}

    // the view mention link is in amcharts, and we couldn't use our normal way to bind them
    @HostListener('click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            let chartDiv = event;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(event.href, null, chartModel.clone(), true);
            }
            return false;
        }
    }

    ngOnInit(): void {
        this.selectedPositiveMapChartDropdownOption = this.mapChartDropdownOptions[0];
        this.selectedNegativeMapChartDropdownOption = this.mapChartDropdownOptions[0];
        const resourceLoaded$: Observable<any> = merge(
            this.topicsService.currentResource$.pipe(
                filter(x => !!x),
                tap(x => this.baseMediaTypes = x.searchCriteria.query.includedMediaTypes)
            ),
            this.folderService.currentResource$,
            this.mediaSubscriptionService.currentResource$.pipe(filter(x => !!x))
        );
        this.subscriptions.push(
            // HACK to show initial loading, because sometimes resourceLoaded$ takes a long time (ex: all saved coverage)
            this.madSentimentService.getSeriesMap().pipe(filter(x => !!x),take(1)).subscribe((seriesMap) => {
                this.seriesMap = seriesMap;
                this.setupSeriesMap();
                this.dashboardService.fulfillSeries(Array.from(seriesMap.values()), false);
            }),
            combineLatest([
                resourceLoaded$,
                this.madSentimentService.getSeriesMap().pipe(filter(x => !!x))
            ]).subscribe(([loaded, seriesMap]) => {
                if (this.seriesMap) {
                    this.dashboardService.deregisterSeries(Array.from(this.seriesMap.values()));
                }
                this.seriesMap = seriesMap;
                this.setupSeriesMap();
                this.reportSeriesReady.emit(this.seriesMap);
                this.refreshClassToChartModelMap();
                this.preCacheSnapDomExports();
            }),
            this.currentUserStateService.getUserCurrency().subscribe(x => this.currencyCode = x),
            this.dashboardService.getDateQuery().pipe(skip(1)).subscribe(() => {
                if (this.seriesMap) {
                    this.setupTrendSeries();
                    this.refreshClassToChartModelMap();
                }
            })
        );
    }



    /*
        this is to help generate exports faster, we preCache them, so snapdom doesn't do a full load on export
    */
    preCacheSnapDomExports(): void {
        setTimeout(() => {
            ['madSentimentTrend', 'madSentimentTopMentions'].forEach(id =>
                this.chartExportService.preCacheSnapDom(document.getElementById(id))
            );
        }, 1000);
    }

    setupSeriesMap() {
        this.positiveCardSeries = this.positiveCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.neutralCardSeries = this.neutralCardSeriesTypes.map(x => this.seriesMap.get(x));
        this.negativeCardSeries = this.negativeCardSeriesTypes.map(x => this.seriesMap.get(x));

        // SENTIMENT BY MEDIA TYPE CHART
        const sentimentByMediaTypeChartSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.sentimentByMediaType);
        sentimentByMediaTypeChartSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            this.baseMediaTypes.length > 0 ? this.baseMediaTypes : MediaTypeModel.getAllMediaTypes();
        const allSentimentTypes = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.negative,
            SearchCriteriaQuerySentimentsEnum.notAvailable,
        ];
        sentimentByMediaTypeChartSeries.sentiments = allSentimentTypes;
        const sentimentByMediaTypeChartStyles = new ChartStylesModel();
        sentimentByMediaTypeChartStyles.customPadding = {
            top: 10,
            right: 15,
            bottom: 15,
            left: 10
        };
        sentimentByMediaTypeChartStyles['legendPosition'] = 'bottom';
        sentimentByMediaTypeChartStyles['legendContentAlign'] = 'center';
        sentimentByMediaTypeChartStyles['legendItemWidth'] = 120;
        const sentimentColorMap = {};
        const sentimentColorTypes = allSentimentTypes;
        sentimentColorTypes.forEach(color => {
            sentimentColorMap[color] = this.sentimentsToChartColorsMap.get(color) || stackedBarColorsConfig[color];
        });
        sentimentByMediaTypeChartStyles.colorMap = sentimentColorMap;
        const sentimentByMediaTypeChartOptions = {
            chartSpecificType: ChartSpecificTypesEnum.MULTI_STACKED_COLUMN,
            stacked: true,
            stackType: StackTypesEnum.percent,
            hideLegend: false,
            styles: sentimentByMediaTypeChartStyles,
            showGrid: true
        } as CategoryValueXYChartOptions;
        const sentimentByMediaTypeChartMetadata = {
            type: ReportBlockTypeEnum.SENTIMENT_BY_MEDIA_TYPE,
            enableDataDisplaySettings: true,
            labelDisplayOptions: [LabelDisplayOptionsEnum.percent]
        } as ChartMetadataModel;
        if (this.selectedSentimentByMediaChartTabIndex === 0) {
            sentimentByMediaTypeChartOptions.stackType = StackTypesEnum.percent;
            sentimentByMediaTypeChartMetadata.labelDisplayOptions = [LabelDisplayOptionsEnum.percent];
        } else {
            sentimentByMediaTypeChartOptions.stackType = StackTypesEnum.count;
            sentimentByMediaTypeChartMetadata.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        }
        this.sentimentByMediaTypeChart = new CategoryValueXYChart(
            [sentimentByMediaTypeChartSeries],
            sentimentByMediaTypeChartOptions,
            sentimentByMediaTypeChartMetadata,
            this.reportsChartSeriesConverterService.getStackedColumnChartSeries(ReportBlockTypeEnum.SENTIMENT_BY_MEDIA_TYPE, allSentimentTypes)
        );

        // POSITIVE MAP CHART
        const positiveMapChartSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveMap);
        positiveMapChartSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
        this.setMapChartMetricFromOptions(positiveMapChartSeries, this.selectedPositiveMapChartDropdownOption);
        const positiveMapStyles = new ChartStylesModel();
        positiveMapStyles.colorMap = {
            choroplethHighValue: chartColors.agprPositiveGreenDark,
            choroplethLowValue: chartColors.agprPositiveGreenLight
        };
        positiveMapStyles.initialLatitude = 5;
        this.positiveMapChart = new MapChartModel(
            [positiveMapChartSeries],
            {
                chartSpecificType: ChartSpecificTypesEnum.MAP,
                colorScheme: MapChartColorSchemeEnum.CHOROPLETH,
                header: false,
                styles: positiveMapStyles
            } as AbstractChartOptionsModel,
            {
                type: ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
            } as ChartMetadataModel
        );

        // NEGATIVE MAP CHART
        const negativeMapChartSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeMap);
        negativeMapChartSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
        this.setMapChartMetricFromOptions(negativeMapChartSeries, this.selectedNegativeMapChartDropdownOption);
        const negativeMapStyles = new ChartStylesModel();
        negativeMapStyles.colorMap = {
            choroplethHighValue: chartColors.agprNegativeRedDark,
            choroplethLowValue: chartColors.agprNegativeRedLight
        };
        negativeMapStyles.initialLatitude = 5;
        this.negativeMapChart = new MapChartModel(
            [negativeMapChartSeries],
            {
                chartSpecificType: ChartSpecificTypesEnum.MAP,
                colorScheme: MapChartColorSchemeEnum.CHOROPLETH,
                header: false,
                styles: negativeMapStyles
            } as AbstractChartOptionsModel,
            {
                type: ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME
            } as ChartMetadataModel
        );

        // TOP MENTIONS
        this.topMentionsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.topMentions);
        this.topMentionsSeries.mentions.filters = new MentionSearchModel();
        let sortField;
        switch (this.selectedTopMentionsSortMetric) {
            case MADChartOptionsEnum.IMPRESSIONS:
                sortField = SearchCriteriaSortFieldEnum.audience_reach;
                break;
            case MADChartOptionsEnum.AVE:
                sortField = SearchCriteriaSortFieldEnum.ave;
                break;
            case MADChartOptionsEnum.DOMAIN_AUTHORITY:
                sortField = SearchCriteriaSortFieldEnum.domainAuthority;
                break;
            default:
                sortField = SearchCriteriaSortFieldEnum.audience_reach;
        }
        this.topMentionsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: sortField,
                pageSize: 5
            };
        };
        switch (this.selectedTopMentionsSentiment) {
            case MADChartOptionsEnum.POSITIVE:
                this.topMentionsSeries.mentions.filters.searchCriteria.query.sentiments =
                    [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
                break;
            case MADChartOptionsEnum.NEGATIVE:
                this.topMentionsSeries.mentions.filters.searchCriteria.query.sentiments =
                    [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
                break;
            default:
                this.topMentionsSeries.mentions.filters.searchCriteria.query.sentiments =
                    [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
        }

        // TOP TERMS - POSITIVE & NEGATIVE
        this.positiveTopTermsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveTopTerms);
        this.positiveTopTermsSeries.mentions.filters = new MentionSearchModel();
        this.positiveTopTermsSeries.mentions.filters.searchCriteria.query.sentiments = [
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)
        ];
        this.positiveTopTermsSeries.color = this.sentimentsToChartColorsMap.get(SearchCriteriaQuerySentimentsEnum.positive);
        this.positiveTopTermsSeries.termType = TermTypesEnum.WORDS;

        this.negativeTopTermsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeTopTerms);
        this.negativeTopTermsSeries.mentions.filters = new MentionSearchModel();
        this.negativeTopTermsSeries.mentions.filters.searchCriteria.query.sentiments = [
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)
        ];
        this.negativeTopTermsSeries.termType = TermTypesEnum.WORDS;
        this.negativeTopTermsSeries.color = this.sentimentsToChartColorsMap.get(SearchCriteriaQuerySentimentsEnum.negative);
        this.topTermsChartOptions = {
            numOfItems: 5,
            tooltipDescription1: this.localizationService.transform(
                `Common.charts.topTerms.tooltipLabel.part1.${SocialMetricAnalysisTypesEnum.volume}`
            ),
            tooltipDescription2: this.localizationService.transform(
                `Common.charts.topTerms.tooltipLabel.part2.${SocialMetricAnalysisTypesEnum.volume}`
            ),
            viewMentionString: this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel')
        };

        this.setupTrendSeries();

        this.setupTopOutlets();
        this.setupTopAuthors();

        this.setupPositiveTopTerms();
        this.setupNegativeTopTerms();
    }

    setupPositiveTopTerms() {
        if (this.positiveTopTermSubscription) {
            this.positiveTopTermSubscription.unsubscribe();
            this.positiveTopTermSubscription = null;
        }
        this.positiveTopTermSubscription = this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveTopTerms).dataProvider.subscribe(data => {
            this.currentSelectedPositiveTerm = data?.[0]?.label ?? '';
            if (this.currentSelectedPositiveTerm) {
                const styles = new ChartStylesModel();
                styles.chartHeight = 400;
                styles.colorMap = {
                    barColor: this.sentimentsToChartColorsMap.get(SearchCriteriaQuerySentimentsEnum.positive)
                };
                this.positiveWordCloudSeries = this.setWordCloudSeries(SearchCriteriaQuerySentimentsEnum.positive).clone();
                this.positiveWordCloudChart = new WordCloudChartModel(
                    [this.positiveWordCloudSeries],
                    {
                        styles: styles,
                        numOfItems: 10,
                        viewMentionString: this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel')
                    },
                    {
                        noContentLabel: this.localizationService.transform('Monitor.MAD.chart.noContentLabel')
                    } as ChartMetadataModel
                );
                if (this.classToChartModelMap) {
                    this.classToChartModelMap.set(this.positiveWordCloudChartClass, this.positiveWordCloudChart);
                }
                this.dashboardService.fulfillSeries([this.positiveWordCloudSeries]);
            }
        });
    }

    setupNegativeTopTerms() {
        if (this.negativeTopTermSubscription) {
            this.negativeTopTermSubscription.unsubscribe();
            this.negativeTopTermSubscription = null;
        }
        this.negativeTopTermSubscription = this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeTopTerms).dataProvider.subscribe(data => {
            this.currentSelectedNegativeTerm = data?.[0]?.label ?? '';
            if (this.currentSelectedNegativeTerm) {
                const styles = new ChartStylesModel();
                styles.chartHeight = 400;
                styles.colorMap = {
                    barColor: this.sentimentsToChartColorsMap.get(SearchCriteriaQuerySentimentsEnum.negative)
                };
                this.negativeWordCloudSeries = this.setWordCloudSeries(SearchCriteriaQuerySentimentsEnum.negative).clone();
                this.negativeWordCloudChart = new WordCloudChartModel(
                    [this.negativeWordCloudSeries],
                    {
                        styles: styles,
                        numOfItems: 10,
                        viewMentionString: this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel')
                    },
                    {
                        noContentLabel: this.localizationService.transform('Monitor.MAD.chart.noContentLabel')
                    } as ChartMetadataModel
                );
                if (this.classToChartModelMap) {
                    this.classToChartModelMap.set(this.negativeWordCloudChartClass, this.negativeWordCloudChart);
                }
                this.dashboardService.fulfillSeries([ this.negativeWordCloudSeries]);
            }
        });
    }

    setupTopOutlets() {
        this.positiveOutletsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveOutlets);
        this.positiveOutletsSeries.metric = this.selectedPositiveOutletsMetric;
        this.positiveOutletsSeries.chartType = ReportBlockTypeEnum.TOP_10_OUTLETS;
        this.positiveOutletsSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
        this.positiveOutletsSeries.mentions.facetGenerator = () => {
            return {
                facet: this.madSentimentService.getTopEntitiesNeoFacet(this.positiveOutletsSeries.metric, EntityType.OUTLET),
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        const positiveOutletsDisplayElement = new ChartDisplayElementModel(
            'positiveOutletsDisplayElement',
            [this.positiveOutletsSeries],
            new ChartStylesModel()
        );
        positiveOutletsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
            title: true,
            dateRange: false,
            sourceLink: false,
            entityProfile: true
        });
        positiveOutletsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        this.positiveOutletsBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_OUTLETS),
            positiveOutletsDisplayElement
        );

        this.negativeOutletsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeOutlets);
        this.negativeOutletsSeries.metric = this.selectedNegativeOutletsMetric;
        this.negativeOutletsSeries.chartType = ReportBlockTypeEnum.TOP_10_OUTLETS;
        this.negativeOutletsSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
        this.negativeOutletsSeries.mentions.facetGenerator = () => {
            return {
                facet: this.madSentimentService.getTopEntitiesNeoFacet(this.negativeOutletsSeries.metric, EntityType.OUTLET),
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        const negativeOutletsDisplayElement = new ChartDisplayElementModel(
            'negativeOutletsDisplayElement',
            [this.negativeOutletsSeries],
            new ChartStylesModel()
        );
        negativeOutletsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
            title: true,
            dateRange: false,
            sourceLink: false,
            entityProfile: true
        });
        negativeOutletsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        this.negativeOutletsBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_OUTLETS),
            negativeOutletsDisplayElement
        );
    }

    setupTopAuthors() {
        this.positiveAuthorsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveAuthors);
        this.positiveAuthorsSeries.metric = this.selectedPositiveAuthorsMetric;
        this.positiveAuthorsSeries.chartType = ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME;
        this.positiveAuthorsSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
        this.positiveAuthorsSeries.mentions.facetGenerator = () => {
            return {
                facet: this.madSentimentService.getTopEntitiesNeoFacet(this.positiveAuthorsSeries.metric, EntityType.CONTACT),
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        const positiveAuthorsDisplayElement = new ChartDisplayElementModel(
            'positiveOutletsDisplayElement',
            [this.positiveAuthorsSeries],
            new ChartStylesModel()
        );
        positiveAuthorsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
            title: true,
            dateRange: false,
            sourceLink: false,
            entityProfile: true
        });
        positiveAuthorsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        this.positiveAuthorsBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME),
            positiveAuthorsDisplayElement
        );

        this.negativeAuthorsSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeAuthors);
        this.negativeAuthorsSeries.metric = this.selectedNegativeAuthorsMetric;
        this.negativeAuthorsSeries.chartType = ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME;
        this.negativeAuthorsSeries.mentions.filters.searchCriteria.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
        this.negativeAuthorsSeries.mentions.facetGenerator = () => {
            return {
                facet: this.madSentimentService.getTopEntitiesNeoFacet(this.negativeAuthorsSeries.metric, EntityType.CONTACT),
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        const negativeAuthorsDisplayElement = new ChartDisplayElementModel(
            'negativeOutletsDisplayElement',
            [this.negativeAuthorsSeries],
            new ChartStylesModel()
        );
        negativeAuthorsDisplayElement.chartHeaderSettings = new ChartDisplaySettingsModel({
            title: true,
            dateRange: false,
            sourceLink: false,
            entityProfile: true
        });
        negativeAuthorsDisplayElement.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        this.negativeAuthorsBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME),
            negativeAuthorsDisplayElement
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
        this.positiveTopTermSubscription.unsubscribe();
        this.negativeTopTermSubscription.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.classToChartModelMap = new Map<string, ChartModel | Am5Chart>([
            [this.sentimentByMediaTypeChartClass, this.sentimentByMediaTypeChart],
            [this.positiveMapChartClass, this.positiveMapChart],
            [this.negativeMapChartClass, this.negativeMapChart],
            [this.sentimentTrendChartClass, this.sentimentTrendChart]
        ]);
        this.classToReportSeriesMap = new Map<string, ReportSeriesModel[]>([
            [this.sentimentTrendChartClass, this.sentimentTrendSeries]
        ]);
        if (this.negativeTopTermSubscription) {
            this.classToChartModelMap.set(this.negativeWordCloudChartClass, this.negativeWordCloudChart);
        }
        if (this.positiveTopTermSubscription) {
            this.classToChartModelMap.set(this.positiveWordCloudChartClass, this.positiveWordCloudChart);
        }
    }

    sentimentByMediaTypeChartStackTypeChanged(index: number): void {
        if (index === 0) {
            this.sentimentByMediaTypeChart.options.stackType = StackTypesEnum.percent;
            this.sentimentByMediaTypeChart.metadata.labelDisplayOptions = [LabelDisplayOptionsEnum.percent];
        } else {
            this.sentimentByMediaTypeChart.options.stackType = StackTypesEnum.count;
            this.sentimentByMediaTypeChart.metadata.labelDisplayOptions = [LabelDisplayOptionsEnum.count];
        }
        this.sentimentByMediaTypeChart = this.sentimentByMediaTypeChart.clone(false);
        this.refreshClassToChartModelMap();
    }

    sentimentTrendChartStackTypeChanged(index: number): void {
        this.selectedSentimentTrendChartTabIndex = index;
        this.setupTrendSeries();
        this.refreshClassToChartModelMap();
    }

    updateSentimentTrendChartNoData(event: boolean): void {
        this.sentimentTrendChartNoData = event;
    }

    openAm5ChartDrilldown(event: any): void {
        const url = event.data;
        if (url) {
            let chartDiv = event.element;
            while (chartDiv && !chartDiv.classList.contains('agpr-mad-chartAnchor')) {
                chartDiv = chartDiv.parentElement;
            }
            if (!chartDiv) {
                return;
            }
            const chartModel = this.classToChartModelMap.get(chartDiv.classList[1]);
            if (chartModel) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(
                    url,
                    null,
                    chartModel.clone(),
                    true,
                    null,
                    null,
                    this.classToReportSeriesMap.get(chartDiv.classList[1])
                );
            }
        }
    }

    refreshClassToChartModelMap(): void {
        if (!this.classToChartModelMap) {
            return;
        }
        this.classToChartModelMap.set(this.sentimentByMediaTypeChartClass, this.sentimentByMediaTypeChart);
        this.classToChartModelMap.set(this.positiveMapChartClass, this.positiveMapChart);
        this.classToChartModelMap.set(this.negativeMapChartClass, this.negativeMapChart);
        this.classToChartModelMap.set(this.sentimentTrendChartClass, this.sentimentTrendChart);
    }

    updateSentimentByMediaTypeChartNoData(noData: boolean): void {
        this.sentimentByMediaTypeChartNoData = noData;
    }

    mapChartDropdownOptionChanged(option: DropdownOptionModel, type: MADSentimentSeriesTypesEnum): void {
        const mapSeries = this.seriesMap.get(type);
        const newSeries = mapSeries.clone();
        this.setMapChartMetricFromOptions(newSeries, option);
        if (type === MADSentimentSeriesTypesEnum.positiveMap) {
            const newMapChart = this.positiveMapChart.clone() as MapChartModel;
            newMapChart.series = [newSeries];
            this.positiveMapChart = newMapChart;
        } else {
            const newMapChart = this.negativeMapChart.clone() as MapChartModel;
            newMapChart.series = [newSeries];
            this.negativeMapChart = newMapChart;
        }
        this.refreshClassToChartModelMap();
        this.seriesMap.set(type, newSeries);
        this.reportSeriesChange.emit({ oldSeriesTypes: [type], newSeries: [newSeries] });
    }

    setMapChartMetricFromOptions(series: ReportSeriesModel, option: DropdownOptionModel): void {
        switch (option.value) {
            case MADChartOptionsEnum.VOLUME:
                series.metric = ChartMetricsEnum.volumeOfMentions;
                break;
            case MADChartOptionsEnum.IMPRESSIONS:
                series.metric = ChartMetricsEnum.IMPRESSIONS;
                break;
            case MADChartOptionsEnum.AVE:
                series.metric = ChartMetricsEnum.ave;
                break;
        }
    }

    updatePositiveMapChartNoData(noData: boolean): void {
        this.positiveMapChartNoData = noData;
    }

    updateNegativeMapChartNoData(noData: boolean): void {
        this.negativeMapChartNoData = noData;
    }

    topMentionsSeriesChange(value: {newSeries: ReportSeriesModel; metric?: MADChartOptionsEnum; sentiment?: MADChartOptionsEnum}) {
        this.topMentionsSeries = value.newSeries;
        if (value.sentiment) {
            this.selectedTopMentionsSentiment = value.sentiment;
        }
        if (value.metric) {
            this.selectedTopMentionsSortMetric = value.metric;
        }

        this.reportSeriesChange.emit({ oldSeriesTypes: [MADSentimentSeriesTypesEnum.topMentions], newSeries: [value.newSeries] });
    }

    openTopMentionsDrilldown() {
        const summaryTrendLineStyles = new ChartStylesModel();
        summaryTrendLineStyles.colorMap = {
            'analysisColor1': chartColors.agprBlue,
            'analysisColor2': chartColors.agprPurple,
            'analysisColor3': chartColors.agprLightBlue
        };
        const summaryTrendLineSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.topMentionsSummary);
        summaryTrendLineSeries.metric = this.topMentionsSeries.metric;
        summaryTrendLineSeries.mentions.filters.searchCriteria.query.sentiments = this.topMentionsSeries.mentions.filters.searchCriteria.query.sentiments;
        const data = summaryTrendLineSeries.dataProvider.getValue();
        if (data.length) {
            data.forEach(x => x.valueLabel = this.localizationService.transform('Reports.formField.analysis.' + x.mappingKeys.metric));
        }
        const chartSeries = this.reportsChartSeriesConverterService.getDateValueChartSeries(
            [summaryTrendLineSeries], ReportBlockTypeEnum.COVERAGE_OVER_TIME, ChartSpecificTypesEnum.LINE, this.currencyCode
        );
        const chartDisplayElement = new ChartDisplayElementModel('', [summaryTrendLineSeries], summaryTrendLineStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration('MADSentiment_topMentions', ReportBlockTypeEnum.COVERAGE_OVER_TIME);
        chartConfiguration.noContent.isMessageStyle = true;
        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.COVERAGE_OVER_TIME,
            chartDisplayElement
        );
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfiguration, chartSettings, i, false);
        });
        const am5Chart = new Am5DateValueXYChart(
            chartSeries,
            chartConfiguration,
            chartSettings
        );

        this.reportsChartDetailService.viewCoverageAnalysisDialog(
            summaryTrendLineSeries.getMentionSeriesUrl(),
            null,
            am5Chart.clone(),
            true,
            null,
            null,
            [summaryTrendLineSeries]
        );
    }

    setWordCloudSeries(sentimentType: SearchCriteriaQuerySentimentsEnum): WordCloudSeriesModel {
        const wordCloudSeries = sentimentType === SearchCriteriaQuerySentimentsEnum.positive ?
            this.seriesMap.get(MADSentimentSeriesTypesEnum.positiveWordCloud) as WordCloudSeriesModel :
            this.seriesMap.get(MADSentimentSeriesTypesEnum.negativeWordCloud) as WordCloudSeriesModel;
        wordCloudSeries.sentimentType = sentimentType === SearchCriteriaQuerySentimentsEnum.positive ? 'positive' : 'negative';
        wordCloudSeries.mentions.filters = new MentionSearchModel();

        const keywords = [new MonitoringKeywordGroupModel()];
        keywords[0].and.push(
            sentimentType === SearchCriteriaQuerySentimentsEnum.positive ? this.currentSelectedPositiveTerm : this.currentSelectedNegativeTerm
        );
        wordCloudSeries.mentions.filters.searchCriteria.query.keywordGroups = keywords;
        wordCloudSeries.mentions.filters.searchCriteria.query.sentiments = [ new SentimentModel(sentimentType) ];
        return wordCloudSeries;
    }

    selectTerm(selectedTerm: TopTermEvent, sentimentType: SearchCriteriaQuerySentimentsEnum) {
        if (sentimentType === SearchCriteriaQuerySentimentsEnum.positive) {
            const wordCloudSeries = this.positiveWordCloudSeries;
            this.dashboardService.deregisterSeries([wordCloudSeries]);
            this.currentSelectedPositiveTerm = selectedTerm.label;
            const keywords = [new MonitoringKeywordGroupModel()];
            keywords[0].and.push(this.currentSelectedPositiveTerm);
            wordCloudSeries.mentions.filters.searchCriteria.query.keywordGroups = keywords;
            this.dashboardService.fulfillSeries([wordCloudSeries]);
        } else {
            const wordCloudSeries = this.negativeWordCloudSeries;
            this.dashboardService.deregisterSeries([wordCloudSeries]);
            this.currentSelectedNegativeTerm = selectedTerm.label;
            const keywords = [new MonitoringKeywordGroupModel()];
            keywords[0].and.push(this.currentSelectedNegativeTerm);
            wordCloudSeries.mentions.filters.searchCriteria.query.keywordGroups = keywords;
            this.dashboardService.fulfillSeries([wordCloudSeries]);
        }
    }

    onTopTermsTooltipClicked(event: TopTermEvent, sentimentType: SearchCriteriaQuerySentimentsEnum) {
        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap['accentColor'] = sentimentType === SearchCriteriaQuerySentimentsEnum.positive ?
            this.positiveTopTermsSeries.color : this.negativeTopTermsSeries.color;

        const cloneSeries = sentimentType === SearchCriteriaQuerySentimentsEnum.positive ?
            this.positiveTopTermsSeries.clone() : this.negativeTopTermsSeries.clone();
        cloneSeries.mentions.useCombinedQuery = true;

        const topTermsBlock = new BlockModel(
            null,
            topTermsListBlockType,
            new TopTermsListChartDisplayElementModel(
                null,
                [cloneSeries],
                event.label,
                chartStyles,
                null
            )
        );
        this.reportsChartDetailService.viewCoverageAnalysisDialog(event.url, topTermsBlock, null, true);
    }

    setupTrendSeries(): void {
        const sentimentTrendChartSeries = this.seriesMap.get(MADSentimentSeriesTypesEnum.trend);
        this.dashboardService.getDateQuery().pipe(take(1)).subscribe(dateQuery => {
            sentimentTrendChartSeries.dateRange = new ReportDateRangeModel().setFromDateQuery(dateQuery);
        });
        const sentimentTypes = [
            SearchCriteriaQuerySentimentsEnum.positive,
            SearchCriteriaQuerySentimentsEnum.neutral,
            SearchCriteriaQuerySentimentsEnum.notAvailable,
            SearchCriteriaQuerySentimentsEnum.negative
        ];
        sentimentTrendChartSeries.sentiments = sentimentTypes;
        const sentimentColors = {};
        const sentimentColTypes = sentimentTypes;
        sentimentColTypes.forEach(color => {
            sentimentColors[color] = this.sentimentsToChartColorsMap.get(color) || stackedBarColorsConfig[color];
        });
        sentimentTrendChartSeries.styles.colorMap = sentimentColors;
        this.sentimentTrendSeries = [sentimentTrendChartSeries];

        const stackedChartTypes = this.selectedSentimentTrendChartTabIndex === 0
            ? StackedChartTypesEnum.PERCENTAGE
            : StackedChartTypesEnum.VOLUME;

        const chartStyles = new ChartStylesModel();
        chartStyles.colorMap = sentimentColors;
        chartStyles.chartDisplaySettings = new ChartDisplaySettingsModel({
            title: false,
            dateRange: false,
            sourceLink: false,
            previousPeriod: false,
            multiSource: false,
            trendIndicators: false
        });
        const chartDisplayElement = new ChartDisplayElementModel('', this.sentimentTrendSeries, chartStyles);
        chartDisplayElement.chartSpecificType = ChartSpecificTypesEnum.LINE;
        chartDisplayElement.stackedChartTypes = stackedChartTypes;
        chartDisplayElement.timeInterval = ChartTimeIntervalsEnum.DYNAMIC;

        const sentimentTrendBlock = new BlockModel(
            null,
            reportBlockTypesMap.get(ReportBlockTypeEnum.SENTIMENT_TREND),
            chartDisplayElement
        );
        const chartSeries = this.reportsChartSeriesConverterService.getChartSeries(this.sentimentTrendSeries, sentimentTrendBlock);
        const chartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
            'MADSentiment_sentimentTrend', ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        chartConfiguration.header.hasHeader = false;
        chartConfiguration.noContent.isMessageStyle = true;
        const chartSettings = this.am5ChartSettingsService.getChartSettings(
            ReportBlockTypeEnum.SENTIMENT_TREND, chartDisplayElement
        );
        this.am5ChartTooltipService.setDoLink(true);
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip(
                chartSeries, chartConfiguration, chartSettings, i, false
            );
        });
        this.sentimentTrendChart = new Am5DateValueXYChart(
            chartSeries,
            chartConfiguration,
            chartSettings
        );
    }

    topEntitiesChartSeriesUpdate(value: {newSeries: ReportSeriesModel; entityType: EntityType; sentimentType: SearchCriteriaQuerySentimentsEnum}) {
        let seriesType;
        if (value.entityType === EntityType.CONTACT) {
            if (value.sentimentType === SearchCriteriaQuerySentimentsEnum.positive) {
                seriesType = MADSentimentSeriesTypesEnum.positiveAuthors;
                this.positiveAuthorsSeries = value.newSeries;
                this.selectedPositiveAuthorsMetric = value.newSeries.metric;
                this.positiveAuthorsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSentimentService.getTopEntitiesNeoFacet(this.positiveAuthorsSeries.metric, value.entityType),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
            } else {
                seriesType = MADSentimentSeriesTypesEnum.negativeAuthors;
                this.negativeAuthorsSeries = value.newSeries;
                this.selectedNegativeAuthorsMetric = value.newSeries.metric;
                this.negativeAuthorsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSentimentService.getTopEntitiesNeoFacet(this.negativeAuthorsSeries.metric, value.entityType),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
            }

        } else {
            if (value.sentimentType === SearchCriteriaQuerySentimentsEnum.positive) {
                seriesType = MADSentimentSeriesTypesEnum.positiveOutlets;
                this.positiveOutletsSeries = value.newSeries;
                this.selectedPositiveOutletsMetric = value.newSeries.metric;
                this.positiveOutletsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSentimentService.getTopEntitiesNeoFacet(this.positiveOutletsSeries.metric, value.entityType),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
            } else {
                seriesType = MADSentimentSeriesTypesEnum.negativeOutlets;
                this.negativeOutletsSeries = value.newSeries;
                this.selectedNegativeOutletsMetric = value.newSeries.metric;
                this.negativeOutletsSeries.mentions.facetGenerator = () => {
                    return {
                        facet: this.madSentimentService.getTopEntitiesNeoFacet(this.negativeOutletsSeries.metric, value.entityType),
                        identifier: ReportSeriesFacetGeneratorEnum.neoFacet
                    };
                };
            }
        }
        this.reportSeriesChange.emit({ oldSeriesTypes: [seriesType], newSeries: [value.newSeries] });
    }

    async sentimentTrendExport(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        await this.chartExportService.exportImageSnapDomWithOverrides(
            event.eventKey.toLowerCase() as BlobType,
            elementRef,
            this.prepareExportData(chartType).title,
            [ { selector: '.agpr-madSentiment-exportMenu', style: { display: 'none' } } ]
        );
    }

    async topMentionsExport(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        await this.chartExportService.exportImageSnapDomWithOverrides(
            event.eventKey.toLowerCase() as BlobType,
            elementRef,
            this.prepareExportData(chartType).title,
            topMentionsMADExportStyleOverrides
        );
    }

    public async exportChart(event: ChartExportEventModel, elementRef: HTMLElement, chartType?: ReportBlockTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        let mapType: 'positive' | 'negative' | undefined;
        if (chartType === ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME) {
            if (elementRef.querySelector(`.${this.positiveMapChartClass}`)) {
                mapType = 'positive';
            } else if (elementRef.querySelector(`.${this.negativeMapChartClass}`)) {
                mapType = 'negative';
            }
        }

        let termType: 'positive' | 'negative' | undefined;

        if (chartType === ReportBlockTypeEnum.WORD_CLOUD) {
            if (elementRef.querySelector(`.${this.positiveWordCloudChartClass}`)) {
                termType = 'positive';
            } else if (elementRef.querySelector(`.${this.negativeWordCloudChartClass}`)) {
                termType = 'negative';
            }
        }

        const exportModel = this.prepareExportData(chartType, mapType, termType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            await this.chartExportService.exportImageSnapDomWithOverrides(
                exportType,
                elementRef,
                exportModel.title,
                [ { selector: '.agpr-madSentiment-exportMenu', style: { display: 'none' } } ]
            );
        }
    }

    private prepareExportData(chartType: ReportBlockTypeEnum,  mapType?: 'positive' | 'negative', termType?: 'positive' | 'negative'): SocialChartExportModel {
        switch (chartType) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                return {
                    title: this.localizationService.transform('Monitor.MAD.sentiment.trend'),
                };
            case ReportBlockTypeEnum.SENTIMENT_BY_MEDIA_TYPE:
                return {
                    title: this.localizationService.transform('Monitor.MAD.sentiment.sentimentByMediaType.label'),
                };
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                if (mapType === 'positive') {
                    return {
                        title: this.localizationService.transform('Monitor.MAD.sentiment.positiveMap.label'),
                    };
                } else if (mapType === 'negative') {
                    return {
                        title: this.localizationService.transform('Monitor.MAD.sentiment.negativeMap.label'),
                    };
                }
                break;
            case ReportBlockTypeEnum.WORD_CLOUD:
                if (termType === 'positive') {
                    return {
                        title: this.localizationService.transform('Monitor.MAD.sentiment.positiveTopTerm.label'),
                    };
                } else if (termType === 'negative') {
                    return {
                        title: this.localizationService.transform('Monitor.MAD.sentiment.negativeTopTerm.label'),
                    };
                }
                break;
            case ReportBlockTypeEnum.TOP_MENTIONS:
                return {
                    // eslint-disable-next-line max-len
                    title: `${this.localizationService.transform('Monitor.MAD.topMentions.title') } ${this.localizationService.transform(this.fullOptions[this.selectedTopMentionsSentiment ].label)} ${this.localizationService.transform('Monitor.MAD.topMentions.title2')} ${this.localizationService.transform(this.fullOptions[this.selectedTopMentionsSortMetric ].label)}`,
                };
            default:
                break;
        }
        return;
    }
}
